import { Sim, type SimConfig } from "./sim/sim";
import { DT, OWNER_PLAYER, MAX_ENTITIES } from "./sim/constants";
import { Renderer } from "./render/renderer";
import { Input } from "./ui/input";
import { Hud } from "./ui/hud";
import { CommandLog } from "./sim/commands";
import { audio, weaponSoundClass } from "./render/audio";

// Wires the deterministic sim to rendering, input and HUD. Fixed 30 Hz sim with
// render interpolation (GDD §9.2); the sim never sees wall-clock time.

export class Game {
  readonly sim: Sim;
  readonly renderer: Renderer;
  readonly input: Input;
  readonly hud: Hud;
  readonly log = new CommandLog();
  private canvas: HTMLCanvasElement;
  private raf = 0;
  private last = 0;
  private acc = 0;
  private running = false;
  private lastFireFlash = new Float32Array(MAX_ENTITIES);
  private prevWinner = 0;
  onRestart?: () => void;
  onEnd?: (winner: number) => void;

  constructor(seed: number, cfg: SimConfig) {
    this.sim = new Sim(seed, cfg);
    this.canvas = document.createElement("canvas");
    document.getElementById("app")!.appendChild(this.canvas);
    this.renderer = new Renderer(this.canvas, this.sim);
    this.input = new Input(this.canvas, this.sim, this.renderer.camera);
    this.hud = new Hud(this.sim, this.input, this.renderer.camera);
    this.renderer.resize();
    window.addEventListener("resize", this.onResize);
  }

  private onResize = (): void => {
    this.renderer.resize();
  };

  start(): void {
    this.running = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.loop);
  }

  destroy(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.onResize);
    this.hud.destroy();
    this.canvas.remove();
  }

  /** Attenuation for a world-space sound: full on-screen, faint off-screen. */
  private audioGain(x: number, y: number): number {
    const cam = this.renderer.camera;
    const dx = x - cam.x, dy = y - cam.y;
    const viewR = Math.hypot(cam.screenW, cam.screenH) / (2 * cam.zoom);
    const g = 1 - Math.hypot(dx, dy) / (viewR * 1.35);
    return g < 0.12 ? 0.12 : g > 1 ? 1 : g;
  }

  private fireAudio(): void {
    if (!audio.ready) return;
    const w = this.sim.world;
    const last = this.lastFireFlash;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) { last[e] = 0; continue; }
      const ff = w.fireFlash[e];
      if (ff > last[e] + 0.001) {
        const def = w.def(e);
        if (def.weapon) {
          const fac = def.faction === "feral" ? 1 : 0;
          audio.weapon(weaponSoundClass(def), fac, this.audioGain(w.posX[e], w.posY[e]));
        }
      }
      last[e] = ff;
    }
  }

  private loop = (now: number): void => {
    if (!this.running) return;
    let dt = (now - this.last) / 1000;
    this.last = now;
    if (dt > 0.25) dt = 0.25; // avoid spiral of death after tab-out

    if (!this.input.paused && this.sim.winner === 0) {
      this.acc += dt;
      let steps = 0;
      let first = true;
      while (this.acc >= DT && steps < 6) {
        const cmds = first ? this.input.drain() : [];
        for (const c of cmds) this.log.push(this.sim.world.tick, c);
        this.sim.step(cmds);
        this.acc -= DT;
        steps++;
        first = false;
      }
    } else {
      this.acc = 0;
      this.input.drain(); // discard input while paused / after game over
    }

    // Feed FX + minimap pings + audio, then clear the sim's per-tick buffer.
    const cam = this.renderer.camera;
    for (const ev of this.sim.fxEvents) {
      if (ev.type === "explosion") {
        if (ev.big) this.hud.addPing(ev.x, ev.y, ev.faction === 0 ? "#40e0ff" : "#ff5a3c");
        audio.explosion(ev.radius, !!ev.big, this.audioGain(ev.x, ev.y));
      }
    }
    this.renderer.fx.consume(this.sim.fxEvents);
    this.sim.fxEvents.length = 0;
    this.renderer.fx.update(dt);

    // Weapon reports: detect the rising edge of each unit's muzzle flash (a new
    // shot) render-side — no sim changes, fully deterministic-safe.
    if (!this.input.paused && this.sim.winner === 0) this.fireAudio();

    // Stall / overflow warning blips (throttled inside the audio engine).
    const eco = this.sim.world.eco[OWNER_PLAYER];
    if (eco.stalled) audio.warn("stall");
    else if (eco.metalWasted > 0 || eco.energyWasted > 0) audio.warn("over");

    // Order markers issued this frame → stamped FX.
    for (const m of this.input.drainMarkers()) this.renderer.fx.addMarker(m.x, m.y, m.kind, m.color);

    // Victory / defeat stinger + end-screen (once).
    if (this.sim.winner !== 0 && this.prevWinner === 0) {
      audio.stinger(this.sim.winner === OWNER_PLAYER);
      this.prevWinner = this.sim.winner;
      this.onEnd?.(this.sim.winner);
    }

    cam.update(dt);
    this.input.updateCamera(dt);
    this.input.refresh();

    const alpha = this.input.paused || this.sim.winner !== 0 ? 1 : this.acc / DT;
    this.renderer.render(alpha, {
      selection: this.input.selection,
      placing: this.input.placing ? { defId: this.input.placing.defId, x: this.input.placeX, y: this.input.placeY, snap: this.input.placeSnap } : null,
      selBox: this.input.selBox,
      hovered: this.input.hovered,
    });
    this.hud.update(dt);

    this.raf = requestAnimationFrame(this.loop);
  };
}

export { OWNER_PLAYER };
